// In a real app this would not be imported directly; instead the schema you
// imported above would import and use these common definitions:
import commonDefinitions from 'vets-json-schema/dist/definitions.json';

import footerContent from 'platform/forms/components/FormFooter';
import { GetFormHelp } from '../components/GetFormHelp';
import PreSubmitInfo from '../containers/PreSubmitInfo';
import { submitHandler } from '../utils/helpers';
import {
  militaryBranchComponentTypes,
  militaryBranchTypes,
} from '../constants/benefits';

import manifest from '../manifest.json';

import IntroductionPage from '../containers/IntroductionPage';
import ConfirmationPage from '../containers/ConfirmationPage';

// pages
import goals from '../pages/goals';
import disabilityRating from '../pages/disabilityRating';
import activeDuty from '../pages/activeDuty';
import militaryBranch, {
  getBranchComponentPages,
} from '../pages/militaryBranch';
import militaryServiceTimeServed from '../pages/militaryServiceTimeServed';
import titleTenServiceTime from '../pages/titleTenTimeServed';
import separationHistory from '../pages/separationHistory';
import characterOfDischarge from '../pages/characterOfDischarge';
import currentlyServing from '../pages/currentlyServing';
import expectedSeparation from '../pages/expectedSeparation';

const { fullName, ssn, date, dateRange, usaPhone } = commonDefinitions;

export const isOnReviewPage = currentLocation => {
  return currentLocation?.pathname.includes('/review-and-submit');
};

export const isOnConfirmationPage = currentLocation => {
  return currentLocation?.pathname.includes('/confirmation');
};

export const formConfig = {
  rootUrl: manifest.rootUrl,
  urlPrefix: '/',
  submit: submitHandler,
  trackingPrefix: 'discover-your-benefits-',
  introduction: IntroductionPage,
  confirmation: ConfirmationPage,
  v3SegmentedProgressBar: true,
  stepLabels: 'Goals;Service;Separation;Discharge;Disability;Review',
  formId: 'T-QSTNR',
  customText: {
    submitButtonText: 'Submit',
  },
  preSubmitInfo: {
    CustomComponent: PreSubmitInfo,
    required: true,
    field: 'privacyAgreementAccepted',
  },
  saveInProgress: {
    messages: {
      inProgress: 'Your benefits questionnaire is in progress.',
      expired:
        'Your saved benefits questionnaire has expired. If you want to continue, please start a new questionnaire.',
      saved: 'Your benefits questionnaire has been saved.',
    },
  },
  version: 0,
  prefillEnabled: true,
  savedFormMessages: {
    notFound: 'Please start over to apply for benefits.',
    noAuth: 'Please sign in again to continue your application for benefits.',
  },
  title: 'Discover your benefits',
  defaultDefinitions: {
    fullName,
    ssn,
    date,
    dateRange,
    usaPhone,
  },
  chapters: {
    chapter1: {
      title: 'Goals',
      pages: {
        goals: {
          path: 'goals',
          title: 'Goals',
          uiSchema: goals.uiSchema,
          schema: goals.schema,
        },
      },
    },
    chapter2: {
      title: 'Service',
      pages: {
        militaryServiceTimeServed: {
          path: 'service/time-served',
          title: 'Length of service',
          uiSchema: militaryServiceTimeServed.uiSchema,
          schema: militaryServiceTimeServed.schema,
        },
        militaryBranch: {
          path: 'service/branch-served',
          title: 'Branch',
          uiSchema: militaryBranch.uiSchema,
          schema: militaryBranch.schema,
        },
        ...getBranchComponentPages(),
        titleTenActiveDuty: {
          path: 'service/active-duty',
          title: 'Title 10 service',
          uiSchema: activeDuty.uiSchema,
          schema: activeDuty.schema,
          depends: formData => {
            return Object.values(militaryBranchTypes).some(pageName => {
              return (
                formData[pageName]?.[
                  militaryBranchComponentTypes.NATIONAL_GUARD_SERVICE
                ] ||
                formData[pageName]?.[
                  militaryBranchComponentTypes.RESERVE_SERVICE
                ]
              );
            });
          },
        },
        titleTenTimeServed: {
          path: 'service/title-ten',
          title: 'Length of Title 10 service',
          uiSchema: titleTenServiceTime.uiSchema,
          schema: titleTenServiceTime.schema,
          depends: formData => {
            return formData.titleTenActiveDuty === true;
          },
        },
      },
    },
    chapter3: {
      title: 'Separation',
      pages: {
        currentlyServing: {
          path: 'separation/currently-serving',
          title: 'Current Service Status',
          uiSchema: currentlyServing.uiSchema,
          schema: currentlyServing.schema,
        },
        expectedSeparation: {
          path: 'separation/expected',
          title: 'Expected Separation',
          uiSchema: expectedSeparation.uiSchema,
          schema: expectedSeparation.schema,
          depends: formData =>
            formData.militaryServiceCurrentlyServing === true,
        },
        separationHistory: {
          path: 'separation/history',
          title: 'Past Separation',
          uiSchema: separationHistory.uiSchema,
          schema: separationHistory.schema,
          depends: formData =>
            formData.militaryServiceCurrentlyServing === false,
        },
      },
    },
    chapter4: {
      title: 'Character of Discharge',
      pages: {
        characterOfDischarge: {
          path: 'discharge',
          title: 'Character of discharge',
          uiSchema: characterOfDischarge.uiSchema,
          schema: characterOfDischarge.schema,
        },
      },
    },
    chapter5: {
      title: 'Disability',
      pages: {
        disabilityRating: {
          path: 'disability',
          title: 'Disability',
          uiSchema: disabilityRating.uiSchema,
          schema: disabilityRating.schema,
        },
      },
    },
  },
  footerContent,
  getHelp: GetFormHelp,
};

export default formConfig;
